/*
 * Platform-admin workspace selector — appears in the admin header on
 * workspace-aware pages so a super-admin can scope a global view to a
 * single tenant. Selection lives in the URL as ?workspaceId=… so deep
 * links survive sharing and reload.
 *
 * Pages that opt-in read the param via useSearchParams; this selector
 * is the canonical writer.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";

interface WorkspaceLite {
  id: string;
  label: string;
  status?: string;
  hasConfiguredDhis2?: boolean;
}

export interface WorkspaceSelectorProps {
  /**
   * When true, hide workspaces that don't have a fully-configured DHIS2
   * service profile (no stored credential, or session-only auth which
   * the worker can't use). Defaults to false so the header selector
   * keeps its global behaviour; pages like TallyMetrics opt in.
   */
  configuredOnly?: boolean;
  /** Override the visible label (defaults to "Workspace"). */
  label?: string;
}

export default function WorkspaceSelector({ configuredOnly = false, label = "Workspace" }: WorkspaceSelectorProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspaces, setWorkspaces] = useState<WorkspaceLite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selected = searchParams.get("workspaceId") ?? "";

  useEffect(() => {
    let cancelled = false;
    api.workspace()
      .then((r) => {
        if (cancelled) return;
        const ws = r.workspaces.map((w) => ({
          id: w.id,
          label: (w as { workspaceName?: string; name?: string; slug?: string }).workspaceName
              ?? (w as { name?: string }).name
              ?? (w as { slug?: string }).slug
              ?? w.id,
          status: (w as { status?: string }).status,
          hasConfiguredDhis2: (w as { hasConfiguredDhis2?: boolean }).hasConfiguredDhis2 === true
        }));
        setWorkspaces(ws);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(() => {
    const list = workspaces ?? [];
    return configuredOnly ? list.filter((w) => w.hasConfiguredDhis2) : list;
  }, [workspaces, configuredOnly]);

  function onChange(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("workspaceId", value);
    else next.delete("workspaceId");
    setSearchParams(next, { replace: true });
  }

  if (error) {
    return <span className="ws-selector ws-selector-error" title={error}>workspaces unavailable</span>;
  }
  if (workspaces === null) {
    return <span className="ws-selector ws-selector-loading">loading workspaces…</span>;
  }
  if (options.length === 0) {
    return (
      <span className="ws-selector ws-selector-empty" title={configuredOnly ? "No workspaces with a fully-configured DHIS2 instance yet." : undefined}>
        {configuredOnly ? "no configured workspaces" : "no workspaces"}
      </span>
    );
  }

  return (
    <label className="ws-selector" title={configuredOnly ? "Pick a workspace whose DHIS2 instance is fully configured" : "Scope this view to a workspace"}>
      <span className="ws-selector-label">{label}</span>
      <select value={selected} onChange={(e) => onChange(e.target.value)} aria-label={`${label} scope`}>
        <option value="">— {configuredOnly ? "All configured workspaces" : "Global view"} —</option>
        {options.map((w) => (
          <option key={w.id} value={w.id}>
            {w.label}{w.status && w.status !== "active" ? ` (${w.status})` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}
